import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ByFieldChartProps } from 'c-main/Components/Campaign/CampaignReports/types';
import { FieldReportType } from 'c-main/Components/Campaign/CampaignReports/types';
import type { ReportConfig } from 'c-reports/Types';
import { ReportDateTimeframe } from 'c-reports/Types';
import type {
    Campaign,
    CampaignNoticeKey,
    ElasticSearchAvailableFilters,
    ElasticSearchFilters,
    ElasticSearchGroupByFieldResponse,
    PCAReportByFieldResponse,
} from 'c-sdk';
import { EsPCAReportField, PCAReportField, PCAReportMetric } from 'c-sdk';
import apiClient from 'c-data/apiClient';
import { format } from 'date-fns';
import { useDateUtils } from 'c-hooks';
import {
    OverallCharts,
    OverallLeaderboardCharts,
} from 'c-main/Components/Campaign/CampaignReports/Views/ByField/ChartTypes';

import { useEntityData } from 'c-data';
import {
    groupedByFilterToLeaderboardData,
    performanceStatsToOldCampaignStats,
} from './Transformers';

type Props = ByFieldChartProps & {
    onFiltersUpdated: (filters: ElasticSearchAvailableFilters) => void;
    reportType: FieldReportType;
    refineFilters: boolean;
    filterExtras?: React.ReactNode[];
    fieldFilters?: ElasticSearchFilters;
    fields?: EsPCAReportField[];
    onResponse?: (response, startDate: Date, endDate: Date, timeframe: ReportDateTimeframe) => void;
};

const ESGroupByField: React.FC<Props> = props => {
    const {
        campaignId,
        environment,
        source,
        metric,
        onFiltersUpdated,
        fieldFilters,
        field,
        fields,
        refineFilters,
        reportType,
        onResponse,
        filterExtras,
    } = props;
    const isMultiField = Array.isArray(fields) && fields.length > 1;
    const [pcaData, setPcaData] = useState<ElasticSearchGroupByFieldResponse>(null);
    useEffect(() => {
        onFiltersUpdated(pcaData?.filters);
    }, [pcaData?.filters, onFiltersUpdated]);
    const metricTranslator = useMemo(() => {
        if (metric === PCAReportMetric.Display) {
            return PCAReportMetric.Plays;
        }
        return metric;
    }, [metric]);
    const { getById } = useEntityData('Campaign');
    const campaign = getById({ id: campaignId });
    const campaignType = (campaign as Campaign).type;
    const showVariance = campaignType === 'linear' || campaignType === 'default';
    const includeDisplayCountInLabel = showVariance;
    const { formatDateString } = useDateUtils();
    const leaderboardData = useMemo<PCAReportByFieldResponse>(() => {
        const data = groupedByFilterToLeaderboardData({
            metric: metricTranslator,
            field: field as EsPCAReportField,
            fields: isMultiField ? fields : undefined,
            data: pcaData,
            withResourceId: true,
            foldBadKeysToOther: campaignType === 'dsp',
        });

        /**
         * the keys for each date are returned in `yyyy-MM-dd` date format.
         * we want to reformat the keys so they use the application's date formatting for consistency
         */
        if (
            !isMultiField &&
            (field === EsPCAReportField.StartDate || field === EsPCAReportField.EndDate)
        ) {
            return {
                reportData: Object.entries(data.reportData).reduce(
                    (newData, [dateKey, values]) => ({
                        ...newData,
                        [formatDateString({ date: dateKey })]: values,
                    }),
                    {},
                ),
            };
        }
        return data;
    }, [metricTranslator, field, fields, isMultiField, pcaData, formatDateString, campaignType]);

    const fetchData: ReportConfig<ElasticSearchGroupByFieldResponse>['fetchData'] = useCallback(
        (start, end, timeframe) =>
            apiClient.ReportsElasticSearch.pcaGroupByField({
                campaignId,
                showStats: true,
                startDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(start, 'yyyy-MM-dd')} 00:00:00`,
                endDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(end, 'yyyy-MM-dd')} 23:59:59`,
                ...(isMultiField
                    ? { fields }
                    : {
                          field: (field as EsPCAReportField) ?? EsPCAReportField.StartDate,
                      }),
                environment,
                source,
                fieldFilters,
                dataStatus: fieldFilters?.[PCAReportField.DataStatus] as CampaignNoticeKey[],
            }),
        [campaignId, environment, field, fields, isMultiField, fieldFilters, source],
    );

    const onDataUpdated: ReportConfig<any>['onDataUpdated'] = useCallback(
        (data: ElasticSearchGroupByFieldResponse, start, end, timeframe) => {
            setPcaData(data);
            if (data?.campaign_stats) {
                onResponse(
                    { campaignStats: performanceStatsToOldCampaignStats(data.campaign_stats) },
                    start,
                    end,
                    timeframe,
                );
            }
        },
        [onResponse],
    );

    const actualChartProps = useMemo<ByFieldChartProps>(
        () => ({
            ...props,
            searchTerm: '',
            pcaData: leaderboardData,
            reportWrapperProps: {
                ...props.reportWrapperProps,
                fetchData,
                onDataUpdated,
            },
            allFields: Object.keys(leaderboardData?.reportData ?? {}),
            actualSelectedFieldParts: Object.keys(leaderboardData?.reportData ?? {}),
            showVariance,
        }),
        [props, leaderboardData, fetchData, onDataUpdated, showVariance],
    );

    return (
        <>
            {reportType === FieldReportType.OverallLeaderboard && (
                <OverallLeaderboardCharts
                    {...actualChartProps}
                    filters={fieldFilters || {}}
                    refineFilters={refineFilters}
                    filterExtras={filterExtras}
                    includeDisplayCountInLabel={includeDisplayCountInLabel}
                    fields={isMultiField ? fields : undefined}
                />
            )}
            {reportType === FieldReportType.Overall && (
                <OverallCharts {...actualChartProps} filterExtras={filterExtras} />
            )}
        </>
    );
};

export default ESGroupByField;
